import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  Body,
  Hero,
  HeroEyebrow,
  HeroTitle,
  HeroSub,
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  TwoUp,
  SectionTitle,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
} from './PortcoTheme';

interface PortcoCounts {
  active: number;
  exited: number;
  impaired: number;
  total: number;
}

interface DebtSummary {
  facilityCount: number;
  totalPrincipal: number;
  totalDrawn: number;
  totalUndrawn: number;
  weightedAvgSpreadBps: number;
  sponsorHeldCount: number;
}

interface CovenantCounts {
  passingPortcos: number;
  warningPortcos: number;
  breachedPortcos: number;
  noDataPortcos: number;
  portcosWithCovenants: number;
}

interface RecentMark {
  portcoId: number;
  companyName: string;
  markDate: string;
  markType?: string;
  equityValue?: number;
  enterpriseValue?: number;
}

interface TopPortco {
  portcoId: number;
  companyName: string;
  sector?: string;
  status?: string;
  ownershipPct?: number;
  latestMarkDate?: string;
  latestEquityValue?: number;
  latestEnterpriseValue?: number;
}

interface DashboardSummary {
  portcoCounts: PortcoCounts;
  debtSummary: DebtSummary;
  covenantCounts: CovenantCounts;
  recentMarks: RecentMark[];
  topPortcos: TopPortco[];
}

interface FundOption {
  id: number;
  fundName: string;
  fundType?: string;
  vintageYear?: number;
}

const fmtMoney = (v?: number) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v.toLocaleString();
};

const fmtPct = (v?: number, decimals = 1) =>
  v == null ? '—' : `${v.toFixed(decimals)}%`;

const fmtDate = (iso?: string) => (iso ? iso : '—');

const CovenantBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid ${portcoTokens.hairline};
`;

const CovenantCell = styled.div<{ $tint: string }>`
  padding: 18px 16px;
  border-right: 1px solid ${portcoTokens.hairline};
  background: ${(p) => p.$tint};
  &:last-child { border-right: 0; }
`;

const CovenantCellLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkMuted};
  margin-bottom: 6px;
`;

const CovenantCellValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  font-family: ${portcoTokens.fontMono};
`;

const FundSelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  font-size: 12px;
`;

const FundSelectorLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  font-weight: 600;
`;

const FundSelect = styled.select`
  font-family: inherit;
  font-size: 13px;
  background: ${portcoTokens.surfaceMuted};
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  padding: 6px 10px;
  min-width: 240px;
  cursor: pointer;
  &:focus { outline: 2px solid ${portcoTokens.accent}; outline-offset: 1px; }
`;

const HeatmapLink = styled.button`
  background: transparent;
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  cursor: pointer;
  &:hover { border-color: ${portcoTokens.accent}; color: ${portcoTokens.accent}; }
`;

const RecentMarksList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const RecentMarkRow = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid ${portcoTokens.hairline};
  cursor: pointer;
  &:last-child { border-bottom: 0; }
  &:hover { background: ${portcoTokens.surfaceMuted}; }
`;

const MarkLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MarkCompany = styled.span`
  font-size: 13px;
  color: ${portcoTokens.inkBody};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MarkMeta = styled.span`
  font-size: 11px;
  color: ${portcoTokens.inkMuted};
  font-family: ${portcoTokens.fontMono};
`;

const MarkValue = styled.span`
  font-size: 13px;
  font-family: ${portcoTokens.fontMono};
  color: ${portcoTokens.inkPrimary};
`;

type PortcoStatus = 'ACTIVE' | 'EXITED' | 'IMPAIRED';

const StatusBadge = styled.span<{ $status: PortcoStatus }>`
  display: inline-block;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border: 1px solid ${portcoTokens.hairline};
  background: ${(p) =>
    p.$status === 'IMPAIRED'
      ? portcoTokens.statusBreachedSubtle
      : p.$status === 'EXITED'
        ? portcoTokens.surfaceMuted
        : portcoTokens.statusPassingSubtle};
  color: ${portcoTokens.inkBody};
  font-family: ${portcoTokens.fontSans};
`;

const normaliseStatus = (s?: string): PortcoStatus => {
  if (s === 'EXITED' || s === 'IMPAIRED') return s;
  return 'ACTIVE';
};

/**
 * GP Dashboard — single-pane "morning-of partner workbench".
 *
 * Reads from /api/portco/dashboard/summary which aggregates portco counts,
 * debt totals, covenant rollup, recent marks, and top portcos in one query.
 *
 * Anti-scope: no deal pipeline, no IC memo, no KPI capture form. Per the
 * P2 plan trim, those concerns belong to fund admins; this dashboard is
 * read-only over P1 data.
 *
 * Embedded mode (`embedded={true}`) is used when this screen is hosted inside
 * the Alternatives → Portfolio Companies workbench. It suppresses the outer
 * AxiomFlow brand bar + breadcrumb (workbench already provides nav context)
 * and skips the global style injection so the dark Alts theme isn't overridden.
 * Light-vs-dark chrome harmonization is Task #10.
 */
interface GPDashboardScreenProps {
  embedded?: boolean;
}

const GPDashboardScreen: React.FC<GPDashboardScreenProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [funds, setFunds] = useState<FundOption[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);

  // Load fund list once. Dashboard re-fetches whenever the selected fund changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get<FundOption[]>('/api/alternatives/funds');
        if (!cancelled) setFunds(Array.isArray(r.data) ? r.data : []);
      } catch {
        // Funds list is optional — if the endpoint is unavailable we still
        // render the all-funds dashboard. Don't surface an error for this.
        if (!cancelled) setFunds([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = selectedFundId
          ? `/api/portco/dashboard/summary?fundId=${selectedFundId}`
          : '/api/portco/dashboard/summary';
        const r = await axios.get<DashboardSummary>(url);
        if (!cancelled) setData(r.data);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFundId]);

  const lastMarkDate = useMemo(() => {
    if (!data || data.recentMarks.length === 0) return null;
    return data.recentMarks[0].markDate;
  }, [data]);

  const covenantsAtRisk = useMemo(() => {
    if (!data) return 0;
    return data.covenantCounts.warningPortcos + data.covenantCounts.breachedPortcos;
  }, [data]);

  return (
    <>
      {!embedded && <PortcoGlobal />}
      <Surface className="portco-surface">
        {!embedded && (
          <Header>
            <HeaderInner>
              <HeaderBrand>
                <HeaderBrandMark>AF</HeaderBrandMark>
                <span>AxiomFlow / GP Dashboard</span>
              </HeaderBrand>
              <HeaderCrumb>
                <a
                  href="/portco"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/portco');
                  }}
                >
                  Portfolio Companies
                </a>
                <span> / </span>
                <span>Dashboard</span>
              </HeaderCrumb>
            </HeaderInner>
          </Header>
        )}

        <Body>
          <Hero>
            <HeroEyebrow>GP front-office</HeroEyebrow>
            <HeroTitle>Portfolio at a glance</HeroTitle>
            <HeroSub>
              Single-pane view across active portcos, sponsor-finance debt,
              covenant trajectory, and recent valuation activity. Read-only —
              data flows from P1 lifecycle paths, not duplicated state.
            </HeroSub>
            {funds.length > 0 && (
              <FundSelectorRow>
                <FundSelectorLabel>Fund scope</FundSelectorLabel>
                <FundSelect
                  value={selectedFundId ?? ''}
                  onChange={(e) =>
                    setSelectedFundId(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">All funds ({funds.length})</option>
                  {funds.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.fundName}
                      {f.vintageYear ? ` · ${f.vintageYear}` : ''}
                      {f.fundType ? ` · ${f.fundType}` : ''}
                    </option>
                  ))}
                </FundSelect>
              </FundSelectorRow>
            )}
          </Hero>

          {loading && <Loading>Loading dashboard…</Loading>}
          {!loading && error && <Empty>{error}</Empty>}

          {!loading && data && (
            <>
              <StatStrip>
                <StatCell>
                  <StatLabel>Active portcos</StatLabel>
                  <StatValue>{data.portcoCounts.active}</StatValue>
                  <StatSub>
                    {data.portcoCounts.total} total · {data.portcoCounts.exited} exited
                    {data.portcoCounts.impaired > 0
                      ? ` · ${data.portcoCounts.impaired} impaired`
                      : ''}
                  </StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Total drawn</StatLabel>
                  <StatValue>{fmtMoney(data.debtSummary.totalDrawn)}</StatValue>
                  <StatSub>
                    {data.debtSummary.facilityCount} facilit
                    {data.debtSummary.facilityCount === 1 ? 'y' : 'ies'} · undrawn{' '}
                    {fmtMoney(data.debtSummary.totalUndrawn)}
                  </StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Wavg spread</StatLabel>
                  <StatValue>
                    {data.debtSummary.weightedAvgSpreadBps > 0
                      ? `${data.debtSummary.weightedAvgSpreadBps.toFixed(0)}bp`
                      : '—'}
                  </StatValue>
                  <StatSub>weighted by drawn balance</StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Covenants at risk</StatLabel>
                  <StatValue>{covenantsAtRisk}</StatValue>
                  <StatSub>
                    {data.covenantCounts.breachedPortcos} breached ·{' '}
                    {data.covenantCounts.warningPortcos} warning
                  </StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Last mark</StatLabel>
                  <StatValue style={{ fontSize: 22 }}>{fmtDate(lastMarkDate ?? undefined)}</StatValue>
                  <StatSub>
                    {data.recentMarks.length} recent across the book
                  </StatSub>
                </StatCell>
              </StatStrip>

              <SectionTitle>Covenant exposure</SectionTitle>
              <Panel>
                <PanelHead>
                  <PanelTitle>By portco severity</PanelTitle>
                  <PanelMeta>
                    {data.covenantCounts.portcosWithCovenants} portco
                    {data.covenantCounts.portcosWithCovenants === 1 ? '' : 's'} with covenants
                    <HeatmapLink
                      style={{ marginLeft: 12 }}
                      onClick={() => navigate('/portfolio-management?tab=credit-loans&sub=covenants&scope=PORTCO_DEBT')}
                    >
                      Open Covenant Center →
                    </HeatmapLink>
                  </PanelMeta>
                </PanelHead>
                <CovenantBlock>
                  <CovenantCell $tint={portcoTokens.statusBreachedSubtle}>
                    <CovenantCellLabel>Breached</CovenantCellLabel>
                    <CovenantCellValue>{data.covenantCounts.breachedPortcos}</CovenantCellValue>
                  </CovenantCell>
                  <CovenantCell $tint={portcoTokens.statusWarningSubtle}>
                    <CovenantCellLabel>Warning</CovenantCellLabel>
                    <CovenantCellValue>{data.covenantCounts.warningPortcos}</CovenantCellValue>
                  </CovenantCell>
                  <CovenantCell $tint={portcoTokens.statusPassingSubtle}>
                    <CovenantCellLabel>Passing</CovenantCellLabel>
                    <CovenantCellValue>{data.covenantCounts.passingPortcos}</CovenantCellValue>
                  </CovenantCell>
                  <CovenantCell $tint={portcoTokens.statusNoDataSubtle}>
                    <CovenantCellLabel>No data</CovenantCellLabel>
                    <CovenantCellValue>{data.covenantCounts.noDataPortcos}</CovenantCellValue>
                  </CovenantCell>
                </CovenantBlock>
              </Panel>

              <SectionTitle style={{ marginTop: 32 }}>Activity</SectionTitle>
              <TwoUp>
                <Panel>
                  <PanelHead>
                    <PanelTitle>Top portcos by latest equity</PanelTitle>
                    <PanelMeta>{data.topPortcos.length} shown</PanelMeta>
                  </PanelHead>
                  <PanelBody style={{ padding: 0 }}>
                    {data.topPortcos.length === 0 ? (
                      <Empty>No portfolio companies yet.</Empty>
                    ) : (
                      <Table>
                        <Thead>
                          <tr>
                            <Th>Company</Th>
                            <Th>Sector</Th>
                            <Th>Status</Th>
                            <Th style={{ textAlign: 'right' }}>Own %</Th>
                            <Th style={{ textAlign: 'right' }}>Latest equity</Th>
                            <Th>Mark date</Th>
                          </tr>
                        </Thead>
                        <tbody>
                          {data.topPortcos.map((p) => (
                            <Tr
                              key={p.portcoId}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/portco/${p.portcoId}`)}
                            >
                              <Td>{p.companyName}</Td>
                              <Td>{p.sector || '—'}</Td>
                              <Td>
                                <StatusBadge $status={normaliseStatus(p.status)}>
                                  {normaliseStatus(p.status)}
                                </StatusBadge>
                              </Td>
                              <TdMono style={{ textAlign: 'right' }}>
                                {fmtPct(p.ownershipPct)}
                              </TdMono>
                              <TdMono style={{ textAlign: 'right' }}>
                                {fmtMoney(p.latestEquityValue)}
                              </TdMono>
                              <TdMono>{fmtDate(p.latestMarkDate)}</TdMono>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHead>
                    <PanelTitle>Recent valuation marks</PanelTitle>
                    <PanelMeta>last {data.recentMarks.length}</PanelMeta>
                  </PanelHead>
                  <PanelBody style={{ padding: 0 }}>
                    {data.recentMarks.length === 0 ? (
                      <Empty>No marks recorded yet.</Empty>
                    ) : (
                      <RecentMarksList>
                        {data.recentMarks.map((m, idx) => (
                          <RecentMarkRow
                            key={`${m.portcoId}-${m.markDate}-${idx}`}
                            onClick={() => navigate(`/portco/${m.portcoId}`)}
                          >
                            <MarkLeft>
                              <MarkCompany>{m.companyName}</MarkCompany>
                              <MarkMeta>
                                {fmtDate(m.markDate)} · {m.markType || '—'}
                              </MarkMeta>
                            </MarkLeft>
                            <MarkValue>{fmtMoney(m.equityValue)}</MarkValue>
                          </RecentMarkRow>
                        ))}
                      </RecentMarksList>
                    )}
                  </PanelBody>
                </Panel>
              </TwoUp>
            </>
          )}
        </Body>
      </Surface>
    </>
  );
};

export default GPDashboardScreen;
